import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  Select,
  Grid,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { Sensors, AccessTime, Settings } from "@mui/icons-material";

export default function DeviceInventory() {
  const [activeTab, setActiveTab] = useState("Aquadata");
  const [hoveredTab, setHoveredTab] = useState(null); // 👈 NEW
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [timeRange, setTimeRange] = useState("24h");
  const [signalRange, setSignalRange] = useState("24h");

  // const [currentDevices, setCurrentDevices] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const steps = ["Device Info", "Hardware", "Assignment"];
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleCreateAccount = () => {
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const cards = [
    {
      icon: <Sensors sx={{ color: "#22c55e" }} />,
      bg: "#E6F9EC",
      count: "Excellent",
      countColor: "#22c55e",
      title: "Signal Strength",
      value: "-45 dBm",
    },
    {
      icon: <AccessTime sx={{ color: "#4689f7" }} />,
      bg: "#E6F0FF",
      count: "99.8%",
      countColor: "#22c55e",
      title: "Uptime (24h)",
      value: "23h 57m",
    },
    {
      icon: <Sensors sx={{ color: "#aa58f7" }} />,
      bg: "#F3E8FF",
      count: "30s",
      countColor: "#A855F7",
      title: "Heartbeat Interval",
      value: "2m ago",
    },
    {
      icon: <Settings sx={{ color: "#f97316" }} />,
      bg: "#FFF4E6",
      count: "15ms",
      countColor: "#6B7280",
      title: "Avg Latency",
      value: "12ms",
    },
  ];

  const deviceTabs = [
    {
      label: "Aquadata",
      value: "Aquadata",
      devices: ["Satnavari(Nagpur)", "Sector-62 Noida", "Bihar"],
    },
    {
      label: "Solvista",
      value: "SOlvista",
      devices: ["Gateway Hub 12", "Gateway Hub 15"],
    },
    {
      label: "Controllers",
      value: "controllers",
      devices: ["Main Controller X", "Backup Controller Y"],
    },
  ];
  const chartDataMap = {
    "1h": [
      { time: "14:55", value: 1 },
      { time: "14:56", value: 1 },
      { time: "14:57", value: 0.5 },
      { time: "14:58", value: 1 },
    ],
    "6h": [
      { time: "10:00", value: 1 },
      { time: "11:00", value: 1 },
      { time: "12:00", value: 0.5 },
      { time: "13:00", value: 1 },
    ],
    "12h": [
      { time: "02:00", value: 1 },
      { time: "06:00", value: 0.5 },
      { time: "10:00", value: 1 },
      { time: "14:00", value: 1 },
    ],
    "24h": [
      { time: "14:20", value: 1 },
      { time: "14:21", value: 1 },
      { time: "14:22", value: 1 },
      { time: "14:23", value: 1 },
      { time: "14:24", value: 0.5 },
      { time: "14:25", value: 1 },
      { time: "14:26", value: 1 },
      { time: "14:27", value: 1 },
    ],
  };
  const signalDataMap = {
    "1h": [
      { time: "14:55", value: -42 },
      { time: "14:56", value: -44 },
      { time: "14:57", value: -43 },
      { time: "14:58", value: -45 },
    ],
    "6h": [
      { time: "10:00", value: -43 },
      { time: "11:00", value: -45 },
      { time: "12:00", value: -44 },
      { time: "13:00", value: -46 },
    ],
    "12h": [
      { time: "02:00", value: -44 },
      { time: "06:00", value: -46 },
      { time: "10:00", value: -45 },
      { time: "14:00", value: -43 },
    ],
    "24h": [
      { time: "14:20", value: -42 },
      { time: "14:21", value: -45 },
      { time: "14:22", value: -43 },
      { time: "14:23", value: -46 },
      { time: "14:24", value: -44 },
      { time: "14:25", value: -45 },
      { time: "14:26", value: -43 },
      { time: "14:27", value: -45 },
    ],
  };

  const visibleCards = selectedDevice
    ? cards.filter((c) => c.device === selectedDevice)
    : cards;

  const currentDevices =
    deviceTabs.find((t) => t.value === hoveredTab)?.devices || [];

  useEffect(() => {
    if (openCreateModal) {
      setActiveStep(0); // 👈 RESET TO STEP 1
    }
  }, [openCreateModal]);

  return (
    <Box p={isMobile ? 1 : 0} mt={isMobile ? "2rem" : 0}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ mb: 3, borderBottom: "1px solid #e5e7eb" }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => {
              setActiveTab(val); // ✅ ONLY click changes active tab
              setSelectedDevice(null);
            }}
          >
            {deviceTabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                onMouseEnter={(e) => {
                  // ✅ hover pe menu open
                  setHoveredTab(tab.value); // 👈 value only
                  setAnchorEl(e.currentTarget); // 👈 MUST
                }}
                onClick={(e) => {
                  // ✅ click = active tab
                  setActiveTab(tab.value);
                  setHoveredTab(tab.value);
                  setAnchorEl(e.currentTarget);
                }}
                sx={{
                  fontWeight: activeTab === tab.value ? 600 : 400,
                  cursor: "pointer",
                }}
              />
            ))}
          </Tabs>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(hoveredTab)}
            onClose={() => setHoveredTab(null)}
            PaperProps={{
              onMouseLeave: () => setHoveredTab(null),
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {/* ✅ All Devices */}
            <MenuItem
              selected={selectedDevice === null} // 👈 ACTIVE
              onClick={() => {
                setSelectedDevice(null);
                setActiveTab(hoveredTab); // 👈 SAME TAB ACTIVE
                setHoveredTab(null);
              }}
            >
              All Devices
            </MenuItem>

            <Divider />

            {currentDevices.map((device) => (
              <MenuItem
                key={device}
                selected={selectedDevice === device} // 👈 ACTIVE DEVICE
                onClick={() => {
                  setSelectedDevice(device); // device active
                  setActiveTab(hoveredTab); //TAB ACTIVE
                  setHoveredTab(null); // close menu
                }}
                sx={{
                  fontWeight: selectedDevice === device ? 600 : 400,
                }}
              >
                {device}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box>
          <Button
            style={
              {
                // backgroundColor: "#e4e8f1ff",
                // color: "white",
                // height: "50px"
              }
            }
            onClick={handleCreateAccount}
          >
            {" "}
            + Add Device
          </Button>
        </Box>
      </Box>

      <Typography fontSize={18} fontWeight={600} mb={2}>
        {selectedDevice ? `${selectedDevice} Details` : "All Devices"}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // mobile
            sm: "1fr 1fr", // tablet
            md: "1fr 1fr 1fr 1fr", // desktop
          },
          gap: 3,
        }}
      >
        {visibleCards.map((card, index) => (
          <Box key={index}>
            <Card
              sx={{
                p: 3,
                height: "100%", // equal height
                borderRadius: "16px",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* Top section */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "12px",
                    backgroundColor: card.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>

                <Typography
                  sx={{
                    color: card.countColor,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {card.count}
                </Typography>
              </Box>

              {/* Bottom section */}
              <Box>
                <Typography
                  sx={{
                    mt: 2,
                    color: "#6b7280",
                    fontSize: 14,
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {card.value}
                </Typography>
              </Box>
            </Card>
          </Box>
        ))}
      </Box>

      {/* ------------------ CHART SECTION ------------------ */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // mobile → stacked
            md: "1fr 1fr", // desktop → 2 columns
          },
          gap: 2,
          mt: 8,
        }}
      >
        {/* HEARTBEAT CHART */}
        <Card sx={{ p: 3, borderRadius: 3, height: { xs: 400, md: 320 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 1,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Heartbeat Status
            </Typography>

            <Select
              size="small"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{
                height: 32,
                fontSize: 13,
                borderRadius: 2,
                minWidth: 140,
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ddd",
                },
              }}
            >
              <MenuItem value="1h">Last 1 Hour</MenuItem>
              <MenuItem value="6h">Last 6 Hours</MenuItem>
              <MenuItem value="12h">Last 12 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
            </Select>
          </Box>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={chartDataMap[timeRange]}
              margin={{ top: 20, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="hbFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="10%" stopColor="#10B981" stopOpacity={0.25} />
                  <stop offset="90%" stopColor="#10B981" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <XAxis dataKey="time" axisLine={false} tickLine={false} />
              <YAxis
                ticks={[0, 0.5, 1]}
                tickFormatter={(v) =>
                  v === 1 ? "Online" : v === 0.5 ? "Delayed" : "Offline"
                }
                axisLine={false}
                tickLine={false}
              />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10B981"
                fill="url(#hbFill)"
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* SIGNAL STRENGTH CHART */}
        <Card sx={{ p: 3, borderRadius: 3, height: { xs: 400, md: 320 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 1,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Signal Strength
            </Typography>

            <Select
              size="small"
              value={signalRange}
              onChange={(e) => setSignalRange(e.target.value)}
              sx={{
                height: 32,
                fontSize: 13,
                borderRadius: 2,
                minWidth: 140,
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ddd",
                },
              }}
            >
              <MenuItem value="1h">Last 1 Hour</MenuItem>
              <MenuItem value="6h">Last 6 Hours</MenuItem>
              <MenuItem value="12h">Last 12 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
            </Select>
          </Box>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={[
                { time: "14:20", value: -42 },
                { time: "14:21", value: -45 },
                { time: "14:22", value: -43 },
                { time: "14:23", value: -46 },
                { time: "14:24", value: -44 },
                { time: "14:25", value: -45 },
                { time: "14:26", value: -43 },
                { time: "14:27", value: -45 },
              ]}
              margin={{ top: 30, right: 20, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="signalFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="10%" stopColor="#3B82F6" stopOpacity={0.25} />
                  <stop offset="90%" stopColor="#3B82F6" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <XAxis dataKey="time" axisLine={false} tickLine={false} />
              <YAxis domain={[-50, -40]} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#3B82F6"
                fill="url(#signalFill)"
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // mobile → stacked
            md: "1fr 1fr", // desktop → two columns
          },
          gap: 2,
          mt: 3,
        }}
      >
        {/* LEFT — DEVICE INFO TABLE */}
        <Card sx={{ p: 3, borderRadius: 3, height: 335 }}>
          {[
            { label: "Protocol", value: "MQTT 3.1.1" },
            { label: "Network Type", value: "WiFi 802.11n" },
            { label: "IP Address", value: "192.168.1.145" },
            { label: "MAC Address", value: "AA:BB:CC:DD:EE:FF" },
            { label: "Gateway", value: "Gateway Hub 12" },
          ].map((row, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1,
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography>{row.label}</Typography>
              <Typography fontWeight={600}>{row.value}</Typography>
            </Box>
          ))}

          {/* Security */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography>Security</Typography>
            <Typography
              fontWeight={600}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#22C55E",
                }}
              />
              TLS 1.3
            </Typography>
          </Box>

          {/* Last Connection */}
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
            <Typography>Last Connection</Typography>
            <Typography fontWeight={600}>2024-12-11 14:25:30</Typography>
          </Box>
        </Card>

        {/* RIGHT — HEARTBEAT EVENTS */}
        <Card sx={{ p: 3, borderRadius: 3, height: 335, overflowY: "auto" }}>
          {[
            {
              status: "Heartbeat Received",
              time: "14:27:15",
              color: "#22C55E",
              bg: "#ECFDF5",
            },
            {
              status: "Heartbeat Received",
              time: "14:26:45",
              color: "#22C55E",
              bg: "#ECFDF5",
            },
            {
              status: "Heartbeat Received",
              time: "14:26:15",
              color: "#22C55E",
              bg: "#ECFDF5",
            },
            {
              status: "Heartbeat Received",
              time: "14:25:45",
              color: "#22C55E",
              bg: "#ECFDF5",
            },
            {
              status: "Heartbeat Delayed",
              time: "14:24:30",
              color: "#F97316",
              bg: "#FEF3E7",
            },
            {
              status: "Heartbeat Received",
              time: "14:24:15",
              color: "#22C55E",
              bg: "#ECFDF5",
            },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                borderRadius: 2,
                background: item.bg,
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: item.color,
                  }}
                />
                <Typography>{item.status}</Typography>
              </Box>

              <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
                {item.time}
              </Typography>
            </Box>
          ))}
        </Card>
      </Box>

      <Dialog
        open={openCreateModal}
        onClose={handleCloseCreateModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e5e7eb" }}>
          <Typography fontWeight={700} fontSize={20}>
            Register Device
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Follow steps to onboard device
          </Typography>
        </Box>

        {/* Stepper */}
        <Box sx={{ px: 3, pt: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <DialogContent sx={{ pt: 3 }}>
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Device UID" required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Device Type Code" required fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Device Name" fullWidth />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Serial Number" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="MAC Address" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Model" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Version" fullWidth />
              </Grid>
            </Grid>
          )}

          {activeStep === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Customer ID" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Site ID" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Zone" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Project" fullWidth />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            justifyContent: "space-between",
          }}
        >
          <Button onClick={handleCloseCreateModal}>Cancel</Button>

          <Box>
            {activeStep > 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #2563eb, #1e40af)",
                }}
                onClick={() => {
                  console.log("Device Registered");
                  handleCloseCreateModal();
                }}
              >
                Register Device
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
